import React, { useState } from "react";
import swal from "sweetalert";
import Select from "../../../../shared/Select";
import { commentOptions } from "../SubjectConfiguration";
import Button from "../../../../shared/Button";
import { Spinner } from "reactstrap";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DragIndicator } from "@mui/icons-material";

const CommentConfiguration = ({
  commentFormData,
  setCommentFormData,
  assessorOptions,
  loading,
  commentTableData,
  setCommentTableData,
  assessorRoleOption,
  assessorConfig,
  setDeletedItem,
  isImporting,
}) => {
  const [editableRowId, setEditableRowId] = useState(null);
  const [newEditedData, setNewEditedData] = useState({});

  //handle comment form change
  const handleCommentDataChange = (name, value, option) => {
    if (name === "commenter") {
      if (!option?.roleCode && !option?.subRoleId) {
        setCommentFormData((prev) => ({
          ...prev,
          [name]: option?.value,
          roleCode: "",
          subRoleId: "",
          roleId: "",
        }));
      } else {
        setCommentFormData((prev) => ({
          ...prev,
          commenter: "",
          roleCode: option?.roleCode,
          subRoleId: option?.subRoleId,
          roleId: option?.value,
        }));
      }
      return;
    }

    const updated = { ...commentFormData, [name]: value ?? option?.value };

    setCommentFormData(updated);
  };

  //add comment data to table
  const handleAddCommentToTable = () => {
    if (
      !commentFormData?.name.trim() ||
      commentFormData?.minLen === "" ||
      commentFormData?.maxLen === "" ||
      !commentFormData?.textType ||
      !(commentFormData?.commenter || commentFormData?.roleId)
    )
      return;

    const commentExists = commentTableData?.some(
      (item) => item?.name === commentFormData?.name,
    );

    if (commentExists) {
      swal("Oops", `Comment with name ${commentFormData?.name} already exists`);
      return;
    }

    if (
      commentFormData?.textType === "letter" &&
      Number(commentFormData?.minLen) < 4
    ) {
      swal(
        "Oops",
        "Minimum length for comment type letter cannot be less than 4",
      );
      return;
    }

    if (
      commentFormData?.textType === "word" &&
      Number(commentFormData?.minLen) < 1
    ) {
      swal(
        "Oops",
        "Minimum length for comment type word cannot be less than 1",
      );
      return;
    }

    if (Number(commentFormData?.minLen) > Number(commentFormData?.maxLen)) {
      swal("Oops", "Minimum length cannot be greater than maximum length");
      return;
    }

    setCommentTableData((prev) => [...prev, commentFormData]);

    setCommentFormData((prev) => ({
      ...prev,
      name: "",
      minLen: "",
      maxLen: "",
      textType: "letter",
      commenter: "",
      roleId: "",
      subRoleId: "",
      roleCode: "",
    }));
  };

  //remove comments data from table
  const removeDataFromTable = (index, id) => {
    const newData = commentTableData?.filter((item, idx) => idx !== index);
    setCommentTableData(newData);

    if (id) {
      setDeletedItem((prev) => ({
        ...prev,
        deleteCommentIds: [...prev.deleteCommentIds, id],
      }));
    }
  };

  //edit data handler
  const handleSaveEdit = (id) => {
    setCommentTableData((prev) =>
      prev.map((row) => (row?.id === id ? { ...row, ...newEditedData } : row)),
    );

    setEditableRowId(null);
    setNewEditedData({});
  };

  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    if (
      result?.destination?.droppableId === result?.source?.droppableId &&
      result?.destination?.index === result?.source?.index
    )
      return;

    const items = Array.from(commentTableData);
    const [reorderedItem] = items.splice(result?.source?.index, 1);
    items.splice(result?.destination?.index, 0, reorderedItem);

    setCommentTableData(items);
  };

  return (
    <>
      <div
        style={{ marginTop: "24px", flexDirection: "column" }}
        className="arr-form"
      >
        <div style={{ marginBottom: "0" }} className="arr-heading">
          <h2>Comments Configuration</h2>
        </div>

        <div className="arr-form noShadow">
          <div className="form-input">
            <input
              type="text"
              value={commentFormData?.name ?? ""}
              onChange={(e) => handleCommentDataChange("name", e.target.value)}
              name="name"
              placeholder="Enter comment Name"
              className="form-control"
            />
          </div>
          <div className="form-input">
            <input
              type="number"
              value={commentFormData?.minLen ?? ""}
              onChange={(e) =>
                handleCommentDataChange("minLen", e.target.value)
              }
              min={0}
              onKeyDown={(e) => {
                if (e.key === "e" || e.key === "-") {
                  e.preventDefault();
                }
              }}
              name="minLen"
              placeholder={`Minimun ${commentFormData?.textType}`}
              className="form-control"
            />
          </div>
          <div className="form-input">
            <input
              type="number"
              value={commentFormData?.maxLen ?? ""}
              onChange={(e) =>
                handleCommentDataChange("maxLen", e.target.value)
              }
              min={0}
              onKeyDown={(e) => {
                if (e.key === "e" || e.key === "-") {
                  e.preventDefault();
                }
              }}
              name="maxLen"
              placeholder={`Maximum ${commentFormData?.textType}`}
              className="form-control"
            />
          </div>
          <Select
            options={commentOptions}
            value={
              commentOptions?.find(
                (opt) => opt?.value === commentFormData?.textType,
              ) || null
            }
            onChange={(option) =>
              handleCommentDataChange("textType", null, option)
            }
            placeholder={"Select Comment Type"}
            styles={{ control: { minWidth: "300px" } }}
            isClearable={false}
          />
          <Select
            options={assessorOptions}
            value={
              assessorOptions
                ?.flatMap((group) => group.options || [])
                .find(
                  (option) =>
                    option?.value ===
                    (commentFormData?.commenter || commentFormData?.roleId),
                ) || null
            }
            onChange={(option) =>
              handleCommentDataChange("commenter", null, option)
            }
            isClearable={false}
            placeholder={"Select Assessor"}
            styles={{ control: { minWidth: "300px" } }}
          />

          <Button
            onClick={handleAddCommentToTable}
            disabled={
              !commentFormData?.name ||
              commentFormData?.minLen === "" ||
              commentFormData?.maxLen === "" ||
              !commentFormData?.textType ||
              !(commentFormData?.commenter || commentFormData?.roleId)
            }
            style={{ display: "block" }}
          >
            Add
          </Button>
        </div>

        <div style={{ margin: 0 }} className="arr-table-container">
          <table className="arr-table">
            <thead>
              <tr>
                <th></th>
                <th>Comment Name</th>
                <th>Type</th>
                <th>Min</th>
                <th>Max</th>
                <th>Assessor</th>
                <th></th>
              </tr>
            </thead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="comment-list">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {loading.read || isImporting ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="arr-spinner">
                            <div className="loader">
                              <Spinner size={"md"} color="primary"></Spinner>
                            </div>
                            <div
                              style={{ fontSize: "13px", marginTop: "15px" }}
                            >
                              Please wait !! Processing.....
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : commentTableData?.length > 0 ? (
                      commentTableData?.map((item, index) => {
                        const isEditable = item?.id
                          ? item?.id === editableRowId
                          : null;

                        const currentData = isEditable
                          ? { ...item, ...newEditedData }
                          : item;

                        const staffRole = currentData?.subRoleId
                          ? assessorRoleOption?.find(
                              (opt) => opt.subRoleId === currentData?.subRoleId,
                            )?.label
                          : null;

                        return (
                          <Draggable
                            draggableId={`${item?.name}-${item?.textType}`}
                            key={
                              currentData?.id ||
                              `${item?.name}-${item?.textType}`
                            }
                            index={index}
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <td>
                                  <div style={{ color: "#d1d5db" }}>
                                    <DragIndicator />
                                  </div>
                                </td>
                                <td>
                                  {isEditable ? (
                                    <input
                                      type="text"
                                      value={currentData?.name ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        setNewEditedData((prev) => ({
                                          ...prev,
                                          name: value,
                                        }));
                                      }}
                                      name="name"
                                      placeholder="Enter comment Name"
                                      className="form-control"
                                      style={{ minWidth: "260px" }}
                                    />
                                  ) : (
                                    currentData?.name
                                  )}
                                </td>
                                <td>
                                  {isEditable ? (
                                    <Select
                                      options={commentOptions}
                                      value={
                                        commentOptions?.find(
                                          (opt) =>
                                            opt?.value ===
                                            currentData?.textType,
                                        ) || null
                                      }
                                      onChange={(option) => {
                                        const value = option?.value;
                                        setNewEditedData((prev) => ({
                                          ...prev,
                                          textType: value,
                                        }));
                                      }}
                                      placeholder={"Select Comment Type"}
                                      styles={{
                                        control: { minWidth: "200px" },
                                      }}
                                      isClearable={false}
                                    />
                                  ) : (
                                    currentData?.textType
                                  )}
                                </td>
                                <td>
                                  {isEditable ? (
                                    <input
                                      type="number"
                                      value={currentData?.minLen ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setNewEditedData((prev) => ({
                                          ...prev,
                                          minLen: value,
                                        }));
                                      }}
                                      min={0}
                                      onKeyDown={(e) => {
                                        if (e.key === "e" || e.key === "-") {
                                          e.preventDefault();
                                        }
                                      }}
                                      name="minLen"
                                      placeholder={`Minimun ${currentData?.textType}`}
                                      className="form-control"
                                      style={{ minWidth: "150px" }}
                                    />
                                  ) : (
                                    currentData?.minLen
                                  )}
                                </td>
                                <td>
                                  {isEditable ? (
                                    <input
                                      type="number"
                                      value={currentData?.maxLen ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setNewEditedData((prev) => ({
                                          ...prev,
                                          maxLen: value,
                                        }));
                                      }}
                                      min={0}
                                      onKeyDown={(e) => {
                                        if (e.key === "e" || e.key === "-") {
                                          e.preventDefault();
                                        }
                                      }}
                                      name="maxLen"
                                      placeholder={`Maximum ${currentData?.textType}`}
                                      className="form-control"
                                      style={{ minWidth: "150px" }}
                                    />
                                  ) : (
                                    currentData?.maxLen
                                  )}
                                </td>
                                <td>
                                  {isEditable ? (
                                    <Select
                                      options={assessorOptions}
                                      value={
                                        assessorOptions
                                          ?.flatMap(
                                            (group) => group.options || [],
                                          )
                                          .find(
                                            (option) =>
                                              (option?.subRoleId &&
                                                option.subRoleId ===
                                                  newEditedData?.subRoleId) ||
                                              option?.value ===
                                                newEditedData?.commenter,
                                          ) || null
                                      }
                                      onChange={(option) => {
                                        if (option?.subRoleId) {
                                          setNewEditedData((prev) => ({
                                            ...prev,
                                            commenter: null,
                                            roleCode: option?.roleCode,
                                            subRoleId: option?.subRoleId,
                                            roleId: option?.value,
                                          }));
                                        } else {
                                          setNewEditedData((prev) => ({
                                            ...prev,
                                            commenter: option?.value,
                                            roleCode: null,
                                            subRoleId: null,
                                            roleId: null,
                                          }));
                                        }
                                      }}
                                      placeholder={"Select Assessor"}
                                      styles={{
                                        control: { minWidth: "200px" },
                                      }}
                                      isClearable={false}
                                    />
                                  ) : (
                                    (staffRole ??
                                    assessorConfig[currentData?.commenter]
                                      ?.people?.name)
                                  )}
                                </td>
                                <td>
                                  <div className="action-btns">
                                    {currentData?.id && isEditable && (
                                      <>
                                        <Button
                                          onClick={() => {
                                            setEditableRowId(null);
                                            setNewEditedData({});
                                          }}
                                          size="sm"
                                          variant="outline"
                                        >
                                          Cancel
                                        </Button>

                                        <Button
                                          onClick={() =>
                                            handleSaveEdit(currentData?.id)
                                          }
                                          size="sm"
                                        >
                                          Save
                                        </Button>
                                      </>
                                    )}

                                    {currentData?.id && !isEditable && (
                                      <Button
                                        onClick={() => {
                                          setEditableRowId(currentData?.id);
                                          setNewEditedData(currentData);
                                        }}
                                        size="sm"
                                        variant="outline"
                                      >
                                        Edit
                                      </Button>
                                    )}

                                    {!isEditable && (
                                      <Button
                                        onClick={() =>
                                          removeDataFromTable(
                                            index,
                                            currentData?.id,
                                          )
                                        }
                                        size="sm"
                                        variant="danger"
                                        disabled={currentData?.recorded}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        );
                      })
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={7}>
                          No Data Found
                        </td>
                      </tr>
                    )}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      </div>
    </>
  );
};

export default CommentConfiguration;
